// Dealer Stock service.
//
// Talks to the MAIN marketing-site backend (dealer.importjdmmagic.com), which is
// a different backend/DB from the auction portal's own API (api.glojapan.com).
// These endpoints are public (no auth) and serve the same data shown on
// https://www.importjdmmagic.com/dealer-stock, so this module intentionally uses
// plain `fetch` rather than the authenticated axios handler.
//
// Dollar prices come finished from that backend (`usdPrice` on every listing,
// /api/listings/prices for garage snapshots), so the USD prices shown here are
// the marketing site's own figures. The portal does no yen conversion: the
// discount and the exchange rate live only in the main site's server.js.
//
// THE BACKEND CONTRACT IS KNOWN, NOT GUESSED.
// Earlier revisions of this file carried "ASSUMPTION (unverified — external API)"
// comments because the marketing backend was treated as a black box. It is not:
// it is server.js in the magic-motorsports repo, and the three open questions
// have been read off `app.get('/api/cars')` there and resolved below.
//   - minPrice/maxPrice are USD.  server.js converts to JPY for carlistings via
//     buildDealerPriceClause() and applies the dollar figure as-is to
//     currentInventory. Send dollars; do not pre-convert.
//   - mileage units differ per collection.  carlistings stores THOUSANDS of km,
//     currentInventory stores real km; server.js divides by 1000 and 1
//     respectively (buildMileageClause). Send real km; render with the "k"
//     suffix only for dealer stock.
//   - there is NO `sort` query param.  /api/cars orders by priority-model,
//     then isPriceZero, then lastUpdated, and ignores anything else. A previous
//     revision mapped sortOrder onto a `sort` param; that was a silent no-op and
//     has been removed. See sortCarsClientSide() for what actually orders the UI.

import { DEALER_STOCK_API_BASE_URL as BASE } from '../config/constants';
import { mileageInKm } from '../helper/dealerStockFilters';

/**
 * Normalize a current-inventory item into the dealer-stock shape.
 * Mirrors the transform in magic-motorsports/src/utils/api.js (fetchListingById).
 */
const normalizeInventoryItem = (item) => {
  const data = { ...item };

  // images: [{url, key, caption, isPrimary}] -> imageURLs: [url] (primary first)
  if (Array.isArray(data.images)) {
    const sorted = [...data.images].sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return 0;
    });
    data.imageURLs = sorted.map((img) => img.url);
  } else if (!data.imageURLs) {
    data.imageURLs = [];
  }

  // current inventory stores USD in `price`
  if (data.price && !data.vehiclePrice) {
    data.vehiclePrice = data.price;
  }

  // Parse "1993 Nissan Skyline" style titles into year/make/model when missing
  if (data.title && (!data.make || !data.model)) {
    const parts = data.title.split(' ');
    if (parts.length >= 3) {
      data.year = data.year || parseInt(parts[0], 10);
      data.make = data.make || parts[1];
      data.model = data.model || parts.slice(2).join(' ');
    }
  }

  data.grade = data.description || data.grade || 'No description available';
  data.mileage = data.mileage || 0;
  data.transmission = data.transmission || 'Manual';
  data.color = data.color || 'Unknown';
  data.bodyType = data.bodyType || 'Car';
  // Flag the source so price logic treats `vehiclePrice` as USD (already converted).
  data.source = 'current-inventory';

  return data;
};

/**
 * Every filter key /api/cars understands, minus pagination and search.
 * Anything outside this set is dropped rather than forwarded, so a typo in a
 * caller cannot silently widen the result set (an unknown param is ignored by
 * the backend, which looks identical to "filter had no effect").
 */
export const DEALER_FILTER_KEYS = [
  'make',
  'model',
  'minYear',
  'maxYear',
  'color',
  'transmission',
  'bodyType',
  'chassisGeneration',
  'minPrice',
  'maxPrice',
  'minMileage',
  'maxMileage',
];

/**
 * Fetch a page of dealer-stock cars (combines dealer stock + current inventory).
 * Mirrors the main site's fetchCars(): GET /api/cars.
 * @param {Object} params - page, limit, search + any key in DEALER_FILTER_KEYS
 * @returns {Promise<{cars: Array, totalCount: number, currentPage: number, totalPages: number}>}
 */
export const fetchDealerStockCars = async (params = {}) => {
  const queryParams = new URLSearchParams();

  ['page', 'limit', 'search'].forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  DEALER_FILTER_KEYS.forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  // NOTE: `disablePriority` is deliberately NOT sent.
  //
  // /api/cars boosts the models this business actually sells — Silvia, Skyline,
  // RX-7, Supra, Chaser, Mark II — and then 1990+ cars, ahead of the plain
  // lastUpdated ordering, unless disablePriority=true turns that off. The
  // marketing site's /dealer-stock never turns it off (its fetchCars in
  // src/utils/api.js appends nothing; only fetchFilteredListings, which hits a
  // different endpoint, and MakeModelPage do). An earlier revision of this file
  // sent disablePriority=true as soon as any filter was set, which meant the
  // portal and the marketing site showed a DIFFERENT first page for the same
  // query — the portal's ordered purely by recency. Same feed, same page,
  // different cars. Leave it off so the two agree.
  const res = await fetch(`${BASE}/api/cars?${queryParams.toString()}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch dealer stock: ${res.status} ${res.statusText}`);
  }
  return res.json();
};

/**
 * The copy the scraper kept when it retired this listing as a duplicate (status
 * 'duplicate', duplicateOf: the kept copy's id). Null when the listing is not
 * one, or the kept copy can't be read or is itself retired, so a bad record can
 * never send the detail page back and forth between two ids.
 */
const fetchKeptCopy = async (listing) => {
  if (listing.status !== 'duplicate' || !listing.duplicateOf) return null;
  try {
    const res = await fetch(`${BASE}/api/listings/${listing.duplicateOf}`);
    if (!res.ok) return null;
    const kept = await res.json();
    return kept.status === 'duplicate' ? null : kept;
  } catch (error) {
    console.error('Error fetching the kept copy of a duplicate listing:', error);
    return null;
  }
};

/**
 * Fetch a single listing by id, normalizing current-inventory items.
 * Mirrors the main site's fetchListingById, except that an id retired as a
 * duplicate returns the kept copy, whose _id differs from the one asked for.
 * The main site gets that from its server's 301.
 * @param {string} id
 * @returns {Promise<Object|null>} the listing, or null if not found
 */
export const fetchDealerStockById = async (id) => {
  try {
    // Dealer stock (carlistings) first
    const res = await fetch(`${BASE}/api/listings/${id}`);
    if (res.ok) {
      const data = await res.json();
      const listing = (await fetchKeptCopy(data)) || data;
      if (!listing.source) listing.source = 'dealer-stock'; // price is JPY
      return listing;
    }

    // Fall back to hand-curated current inventory (S3-backed, USD price)
    if (res.status === 404) {
      const invRes = await fetch(`${BASE}/api/current-inventory/${id}`);
      if (invRes.ok) {
        const inventoryData = await invRes.json();
        return normalizeInventoryItem(inventoryData);
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching dealer stock listing:', error);
    return null;
  }
};

/**
 * Cars similar to this one, for the "You may also like" rail on the detail page.
 * GET /api/cars/:id/similar widens its criteria in steps (same make+model, then
 * make+body, then body+/-5yr, then make) until it has `limit` of them, and
 * returns { similar, originalVehicle }. current-inventory rows come back already
 * shaped like dealer stock (the aggregation maps price->vehiclePrice and
 * images->imageURLs), so only `source` needs to survive for the price helpers.
 * Never throws: an empty rail is the correct failure mode here.
 */
export const fetchSimilarDealerStock = async (id, limit = 6) => {
  try {
    const res = await fetch(`${BASE}/api/cars/${id}/similar?limit=${encodeURIComponent(limit)}`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.similar) ? data.similar : [];
  } catch (error) {
    console.error('Error fetching similar dealer stock:', error);
    return [];
  }
};

/**
 * Fetch filter option lists (makes, models, years, colors, bodyTypes,
 * chassisGenerations) for the dealer-stock grid.
 * @param {Object} filters - optional { make, model } to narrow models/years/etc.
 */
export const fetchDealerStockFilterOptions = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    ['make', 'model'].forEach((key) => {
      const value = filters[key];
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    const qs = queryParams.toString();
    const url = qs
      ? `${BASE}/api/listings/filter-options?${qs}`
      : `${BASE}/api/listings/filter-options`;

    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch filter options: ${res.status}`);
    const data = await res.json();
    return {
      makes: data.makes || [],
      models: data.models || [],
      years: data.years || [],
      colors: data.colors || [],
      bodyTypes: data.bodyTypes || [],
      chassisGenerations: data.chassisGenerations || [],
    };
  } catch (error) {
    console.error('Error fetching dealer stock filter options:', error);
    return { makes: [], models: [], years: [], colors: [], bodyTypes: [], chassisGenerations: [] };
  }
};

/**
 * Today's prices for dealer listings, keyed by id ({ usdPrice, shippedUsd,
 * importedUsd, importedTitledUsd } each), worked out by the main site's server.
 * For records that hold only a stored snapshot of a car (My Garage). Mirrors
 * the main site's fetchListingPrices: GET /api/listings/prices, which answers
 * at most 200 ids per request. {} on any failure, so a caller falls back to the
 * price stored with the snapshot.
 * @param {string[]} ids - Listing ids
 * @returns {Promise<Object>}
 */
export const fetchDealerStockPrices = async (ids) => {
  const unique = [...new Set((ids || []).filter(Boolean).map(String))];
  if (!unique.length) return {};
  try {
    const chunks = [];
    for (let i = 0; i < unique.length; i += 200) chunks.push(unique.slice(i, i + 200));
    const results = await Promise.all(chunks.map(async (chunk) => {
      const res = await fetch(`${BASE}/api/listings/prices?ids=${chunk.map(encodeURIComponent).join(',')}`);
      if (!res.ok) return {};
      const data = await res.json();
      return (data && data.prices) || {};
    }));
    return Object.assign({}, ...results);
  } catch (error) {
    console.error('Error fetching dealer stock prices:', error);
    return {};
  }
};

/**
 * Coerce a possibly-formatted price into a finite number.
 * The API may return the yen price as a formatted string (thousands separators,
 * full-width digits/punctuation), which would make plain arithmetic yield NaN.
 * Strip anything that isn't part of a number, normalize full-width digits to
 * ASCII, then Number(); returns NaN if nothing numeric remains.
 */
const toNumericPrice = (value) => {
  if (typeof value === 'number') return value;
  if (value == null) return NaN;
  const normalized = String(value)
    // full-width digits (０-９) -> ASCII 0-9
    .replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0))
    // drop everything except digits, minus and decimal point
    .replace(/[^0-9.-]/g, '');
  return Number(normalized);
};

/** Format a USD amount; 0 / missing -> "ASK" (price on request). */
export const formatUsd = (amount) => {
  if (!amount) return 'ASK';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Resolve a car's display price in USD.
 * - current-inventory: `vehiclePrice` is already USD.
 * - auction (a car we won at auction for a customer, in the garage): the
 *   snapshot's `usdPrice` is the winning price in USD.
 * - dealer-stock: the main site's `usdPrice`, exactly as it sends it. Its
 *   server takes the discount off the listed yen (10%, or 5% on Ferrari and
 *   Lamborghini) and converts at its own rate, and keeps both out of browser
 *   code on purpose. Converting the yen here instead priced every Ferrari and
 *   Lamborghini about 5% under the main site. No usdPrice -> 0 ("ASK"), as on
 *   the main site.
 */
export const priceForCar = (car) => {
  if (!car) return 0;
  if (car.source === 'current-inventory') {
    // vehiclePrice/price are already USD, but may arrive as a formatted string;
    // coerce so downstream formatting/sorting never sees NaN. 0 -> "ASK".
    const usd = toNumericPrice(car.vehiclePrice || car.price);
    return Number.isFinite(usd) ? usd : 0;
  }
  const usd = toNumericPrice(car.usdPrice);
  return Number.isFinite(usd) && usd > 0 ? usd : 0;
};

// Freight rates keyed on the stored Japanese body-type values, plus the fixed
// inland/export/service fees. Mirrors the freight table + shipping math in
// magic-motorsports/src/pages/VehicleDetail.jsx so the "Shipped Price" shown
// here matches the marketing site exactly. DEFAULT covers unlisted body types.
const FREIGHT_RATES = {
  'SUV・クロスカントリー': 2680,
  'オープン・カブリオレ': 1950,
  'キャブバン': 2590,
  'クーペ・スポーツ': 1950,
  'セダン': 1950,
  'トラック': 3700,
  'ハッチバック': 1760,
  'ハードトップ': 1950,
  'バス': 4820,
  'ピックアップトラック': 3520,
  'ボンネットバン': 2410,
  'ミニバン・ワンボックス': 2960,
  'ワゴン': 2220,
  '軽-RV系': 1630,
  '軽-キャブバン': 1580,
  '軽-トラック': 1480,
  '軽自動車': 1390,
};
const DEFAULT_FREIGHT = 1950;
export const INLAND_SHIPPING = 400;
export const EXPORT_FEE = 500;
export const SERVICE_FEE = 500;

// Match freight on the exact stored Japanese body type, with a substring
// fallback, then the standard DEFAULT rate (same resolution order as the main site).
export const freightForBodyType = (bodyType) => {
  const bt = bodyType || '';
  if (FREIGHT_RATES[bt] != null) return FREIGHT_RATES[bt];
  const partialKey = Object.keys(FREIGHT_RATES).find((key) => bt.includes(key));
  return partialKey ? FREIGHT_RATES[partialKey] : DEFAULT_FREIGHT;
};

/**
 * The itemised shipping quote behind the "Shipped Price" figure, so the detail
 * page can show the buyer what the difference is made of instead of one opaque
 * number. Same four components, in the same order, as the marketing site.
 */
export const shippingBreakdownForCar = (car) => {
  const oceanFreight = freightForBodyType(car && car.bodyType);
  return {
    oceanFreight,
    inlandShipping: INLAND_SHIPPING,
    exportFee: EXPORT_FEE,
    serviceFee: SERVICE_FEE,
    total: oceanFreight + INLAND_SHIPPING + EXPORT_FEE + SERVICE_FEE,
  };
};

/**
 * Shipped (delivered-to-US-port) price in USD: car price + freight + the fixed
 * inland/export/service fees. Mirrors the default "shipped" quote on the
 * marketing site's vehicle detail page. Returns 0 when the car price is unknown
 * (so callers can show "ASK").
 */
export const shippedPriceForCar = (car) => {
  const base = priceForCar(car);
  if (!base) return 0;
  return Math.round((base + shippingBreakdownForCar(car).total) * 100) / 100;
};

// US entry ports offered in the quote form, verbatim from the marketing site.
// The port does NOT change the quote — freight is priced off body type, not
// destination — it rides along so the team knows where to route the car.
export const US_PORTS = [
  'BALTIMORE', 'BRUNSWICK', 'FREEPORT (TEXAS)', 'GALVESTON', 'GUAM', 'HONOLULU',
  'NEWARK', 'JACKSONVILLE', 'LONG BEACH', 'LOS ANGELES', 'NEWPORT NEWS',
  'PORT HUENEME', 'SAIPAN', 'SAN DIEGO', 'TACOMA',
];

// Import-side fees, applied only to an "imported" quote. Rates and flat fees
// copied from calculateQuotePrice in magic-motorsports/src/pages/VehicleDetail.jsx
// so the two sites quote the same number for the same car.
const IMPORT_FEE = 350;
const TERMINAL_HANDLING_FEE = 177.75;
export const TITLE_FEE = 450;
const DUTY_RATE = 0.125;        // 12.5%
const PROCESSING_RATE = 0.00346; // 0.346%
const HARBOR_RATE = 0.00125;     // 0.125%

/**
 * The quote behind the "Get a Quote" panel.
 *
 * Two tiers, matching the marketing site:
 *   shipped   car + ocean freight + Japan inland + export clearance + service.
 *             Delivered to a US port, nothing cleared.
 *   imported  the above plus US duty (12.5% of the car), the merchandise
 *             processing and harbour maintenance fees, terminal handling and
 *             our import fee. Customs-cleared and collectable.
 *
 * The Virginia title fee is only offered on an imported quote — there is
 * nothing to title until the car has cleared customs.
 *
 * Returns 0s when the car price is unknown so callers can show "ASK".
 */
export const quoteForCar = (car, { quoteType = 'shipped', includeTitle = false } = {}) => {
  const vehiclePrice = priceForCar(car);
  const shippingCost = shippingBreakdownForCar(car).total;

  if (!vehiclePrice) {
    return { vehiclePrice: 0, shippingCost, importCost: 0, titleFee: 0, total: 0 };
  }

  const importCost =
    quoteType === 'imported'
      ? IMPORT_FEE
        + TERMINAL_HANDLING_FEE
        + vehiclePrice * DUTY_RATE
        + vehiclePrice * PROCESSING_RATE
        + vehiclePrice * HARBOR_RATE
      : 0;

  // Title is an imported-only add-on; ignore the flag on a shipped quote so a
  // stale checkbox cannot inflate the number.
  const titleFee = quoteType === 'imported' && includeTitle ? TITLE_FEE : 0;

  return {
    vehiclePrice,
    shippingCost,
    importCost,
    titleFee,
    total: Math.round((vehiclePrice + shippingCost + importCost + titleFee) * 100) / 100,
  };
};

/**
 * Order a page of cars for display.
 *
 * This is a CLIENT-SIDE sort of the current page only, and that is a real
 * limitation rather than an oversight: /api/cars has no sort parameter (it
 * orders by priority-model, isPriceZero, then lastUpdated), so neither this
 * portal nor the marketing site — which sorts the same way, in
 * magic-motorsports/src/pages/DealerStock.jsx — can order the full result set.
 * Both surfaces therefore reorder the fetched page and nothing more.
 *
 * Prices are compared in USD (priceForCar) so dealer-stock and
 * current-inventory rows sort against each other correctly.
 */
export const sortCarsClientSide = (cars, sortOrder) => {
  const list = Array.isArray(cars) ? cars : [];
  const priced = list.map((car) => ({ car, usd: priceForCar(car) }));
  priced.sort((a, b) => {
    switch (sortOrder) {
      case 'price-low-high':
        return a.usd - b.usd;
      case 'price-high-low':
        return b.usd - a.usd;
      case 'year-new-old':
        return (b.car.year || 0) - (a.car.year || 0);
      case 'year-old-new':
        return (a.car.year || 0) - (b.car.year || 0);
      // Real kilometres on both sides. carlistings stores mileage in THOUSANDS
      // and currentInventory in whole km, so comparing the raw field ranked a
      // 19,000 km dealer car (stored as 19) below a 19,100 km curated one
      // (stored as 19100) — every current-inventory row was shoved to one end
      // of a mixed page.
      case 'mileage-low-high':
        return mileageInKm(a.car) - mileageInKm(b.car);
      case 'mileage-high-low':
        return mileageInKm(b.car) - mileageInKm(a.car);
      default:
        return 0;
    }
  });
  return priced.map((p) => p.car);
};

/**
 * Which stored image is the car's hero shot, and at what resolution.
 *
 * THE ANSWER IS ALWAYS imageURLs[0]. Mirrors
 * magic-motorsports/src/utils/vehicleImages.js — keep the two in step.
 *
 * This used to drop imageURLs[0] for every scraped listing, on the grounds that
 * they lead with a low-quality frame. That was wrong for both sources, and it is
 * what put dashboards and dealer adverts on the cards.
 *
 *   carsensor  [0] and [1] are two different photos of one gallery and the
 *              scraper has already put the dealer's hero at [0]. Skipping it
 *              promoted frame 2 — nearly always the interior.
 *   goo-net    [0] is the SEARCH-CARD photo, [1] the first DETAIL-GALLERY frame.
 *              Different photographs: by perceptual hash over 54 listings, 53
 *              differed at Hamming 14-38 (the same-image floor is ~5). [0] was an
 *              exterior 3/4 in 12 of 12 sampled, while [1] produced an interior, a
 *              coupon banner and a cropped roof. Dealers routinely upload their
 *              advert as the first GALLERY frame, so skipping [0] is precisely
 *              what surfaced adverts as thumbnails.
 *
 * An earlier version skipped [0] for goo-net only, believing it a low-res
 * duplicate of [1] because a slot number in the two filenames matched. That
 * proves nothing about the pixels. Do not reintroduce it without hashing.
 *
 * The one real wrinkle is resolution: goo-net serves the card photo from a size
 * directory, and a minority of listings still store it at /Q/ (240x180), which
 * renders as a smear. /J/ (640x480) is goo-net's standard gallery size and is
 * present for every listing checked, so /Q/ is promoted to it. Nothing else is
 * rewritten — /H/ is dealer-dependent and 404s for some uploads.
 */
const GOONET_PHOTO_HOST = /^https?:\/\/picture\d+\.goo-net\.com\//i;

/** Promote a goo-net /Q/ (240x180) frame to the /J/ (640x480) copy of the same file. */
const upgradeThumbSize = (url) => {
  const str = String(url || '');
  if (!GOONET_PHOTO_HOST.test(str)) return str;
  return str.replace(/\/Q\/(?=[^/?#]+(?:[?#]|$))/, '/J/');
};

/** The frames a visitor should see, hero first. Nothing is dropped. */
export const galleryImagesForCar = (car) =>
  ((car && Array.isArray(car.imageURLs) && car.imageURLs) || []).map(upgradeThumbSize);
